/* ControlSettingsPage — §57-63 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { controlApi, useOnlineStatus } from "../lib/controlApi";
import { ChevronLeft, Loader2, AlertCircle } from "lucide-react";

export default function ControlSettingsPage() {
  const online = useOnlineStatus();
  const [cfg, setCfg] = useState(null);
  const [autom, setAutom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmPause, setConfirmPause] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, a] = await Promise.all([controlApi.config(), controlApi.automation()]);
      setCfg(c); setAutom(a);
    } catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const changeMode = async (mode) => {
    if (!online) { toast.error("Requiere conexión."); return; }
    if (mode === "paused") { setConfirmPause("paused"); return; }
    try {
      await controlApi.patchAutomation({ mode, expected_version: autom.version });
      toast.success("Modo actualizado.");
      load();
    } catch (ex) {
      const code = ex.response && ex.response.data && ex.response.data.detail && ex.response.data.detail.error && ex.response.data.detail.error.code;
      if (code === "VERSION_CONFLICT") { toast.error("Cambió desde otro dispositivo."); load(); }
      else toast.error("Error");
    }
  };

  const confirmMode = async () => {
    try {
      await controlApi.patchAutomation({ mode: confirmPause, expected_version: autom.version });
      toast.success("Motor pausado.");
      setConfirmPause(null); load();
    } catch { toast.error("Error"); setConfirmPause(null); }
  };

  const toggleChannel = async (channel) => {
    if (!online) { toast.error("Requiere conexión."); return; }
    try {
      const key = channel === "facebook" ? "facebook_enabled" : "instagram_enabled";
      await controlApi.patchAutomation({ mode: autom.mode, [key]: !autom[key], expected_version: autom.version });
      toast.success("Canal actualizado.");
      load();
    } catch { toast.error("Error"); }
  };

  if (loading) return <div className="p-6 text-textgray text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>;

  return (
    <div className="p-4 space-y-4" data-testid="spc-settings">
      <div className="flex items-center gap-2">
        <Link to="/control/more" className="text-textgray"><ChevronLeft className="w-4 h-4" /></Link>
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-textgray">Configuración</div>
          <div className="font-serif text-xl font-bold text-blueblack">Operación</div>
        </div>
      </div>

      {/* Automatización */}
      <div className="border border-softblue bg-white">
        <div className="px-4 py-2 border-b border-softblue text-[10px] uppercase tracking-widest text-textgray font-bold">Automatización</div>
        <div className="p-3 grid grid-cols-3 gap-1">
          {[
            ["automatic", "Automático"],
            ["portal_only", "Solo Portal"],
            ["paused", "Pausado"],
          ].map(([v, l]) => (
            <button key={v} onClick={() => changeMode(v)} data-testid={`spc-mode-${v}`}
              className={`h-11 text-[11px] uppercase tracking-widest font-bold border ${autom.mode === v ? "bg-blueblack text-white border-blueblack" : "bg-white text-blueblack border-softblue"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* Canales */}
      <div className="border border-softblue bg-white">
        <div className="px-4 py-2 border-b border-softblue text-[10px] uppercase tracking-widest text-textgray font-bold">Canales sociales</div>
        {[["facebook", "Facebook", autom.facebook_enabled],
          ["instagram", "Instagram", autom.instagram_enabled]].map(([k, l, on]) => (
          <div key={k} className="p-3 flex items-center justify-between border-t border-softblue first:border-t-0">
            <div>
              <div className="text-sm font-bold text-blueblack">{l}</div>
              <div className={`text-[11px] font-bold ${on ? "text-emerald-700" : "text-slate-600"}`}>
                {on ? "🟢 Activo" : "⚪ Pausado por administrador"}
              </div>
            </div>
            <button onClick={() => toggleChannel(k)} data-testid={`spc-toggle-${k}`}
              className={`h-9 px-4 text-[11px] uppercase tracking-widest font-bold border ${on ? "border-softblue bg-white text-blueblack" : "border-emerald-300 bg-emerald-50 text-emerald-700"}`}>
              {on ? "Pausar" : "Activar"}
            </button>
          </div>
        ))}
      </div>

      {/* Cupos (readonly display, editable in web admin) */}
      <div className="border border-softblue bg-white">
        <div className="px-4 py-2 border-b border-softblue text-[10px] uppercase tracking-widest text-textgray font-bold">Cupos diarios (informativo)</div>
        <div className="grid grid-cols-2 divide-x divide-softblue text-center">
          <div className="p-3">
            <div className="font-serif text-xl font-bold text-blueblack">{cfg.distribution.facebook_daily_target}/{cfg.distribution.facebook_daily_max}</div>
            <div className="text-[10px] uppercase tracking-widest text-textgray">FB target/max</div>
          </div>
          <div className="p-3">
            <div className="font-serif text-xl font-bold text-blueblack">{cfg.distribution.instagram_daily_target}/{cfg.distribution.instagram_daily_max}</div>
            <div className="text-[10px] uppercase tracking-widest text-textgray">IG target/max</div>
          </div>
        </div>
      </div>

      <div className="text-[10px] text-textgray text-center">Tandas: {cfg.franjas.join(" · ")}</div>

      {confirmPause && (
        <div className="fixed inset-0 z-50 bg-blueblack/70 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white w-full max-w-md p-4 space-y-3">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              <div className="font-serif text-lg font-bold text-blueblack">¿Pausar el motor automático?</div>
            </div>
            <p className="text-sm text-textgray">Mientras esté pausado no se realizarán publicaciones automáticas.</p>
            <div className="flex gap-2 pt-2">
              <button onClick={() => setConfirmPause(null)} className="flex-1 h-11 border border-softblue text-blueblack text-[11px] uppercase tracking-widest font-bold">Cancelar</button>
              <button onClick={confirmMode} data-testid="spc-pause-confirm"
                className="flex-1 h-11 bg-amber-600 text-white text-[11px] uppercase tracking-widest font-bold">Pausar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
